import { motion } from "framer-motion";

const LoadingScreen = () => {
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.85) 50%, hsl(var(--accent)/0.3) 100%)",
      }}
    >
      {/* Animated Background Patterns */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating geometric shapes */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-primary-foreground/5"
            style={{
              width: `${100 + i * 80}px`,
              height: `${100 + i * 80}px`,
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 15, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 4 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        ))}
        
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`h-${i}`}
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-foreground to-transparent"
              style={{ top: `${i * 10}%` }}
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 0.3, scaleX: 1 }}
              transition={{ delay: i * 0.1, duration: 1 }}
            />
          ))}
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`v-${i}`}
              className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary-foreground to-transparent"
              style={{ left: `${i * 10}%` }}
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 0.3, scaleY: 1 }}
              transition={{ delay: i * 0.1, duration: 1 }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Elevator Icon Animation */}
        <motion.div
          className="relative mb-8"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 200, 
            damping: 15,
            delay: 0.2 
          }}
        >
          {/* Outer Ring */}
          <motion.div
            className="absolute inset-0 rounded-full border-4 border-accent/30"
            style={{ width: 140, height: 140, margin: -10 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
          
          {/* Inner Ring with dots */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ width: 160, height: 160, margin: -20 }}
          >
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-accent"
                style={{
                  left: "50%",
                  top: "50%",
                  transform: `rotate(${i * 30}deg) translateY(-80px) translateX(-50%)`,
                }}
                animate={{
                  opacity: [0.3, 1, 0.3],
                  scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.1,
                }}
              />
            ))}
          </motion.div>

          {/* Elevator Shaft Container */}
          <div className="relative w-[120px] h-[120px] rounded-2xl bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 overflow-hidden shadow-2xl">
            {/* Shaft interior lines */}
            <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-primary-foreground/20" />
            <div className="absolute right-2 top-0 bottom-0 w-0.5 bg-primary-foreground/20" />
            
            {/* Floor indicators */}
            {[1, 2, 3, 4].map((floor) => (
              <div
                key={floor}
                className="absolute left-0 right-0 h-px bg-primary-foreground/10"
                style={{ top: `${floor * 20}%` }}
              />
            ))}
            
            {/* Cable */}
            <motion.div
              className="absolute left-1/2 -translate-x-1/2 w-0.5 bg-accent/60 origin-top"
              animate={{
                height: ["15%", "70%", "15%"],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            {/* Elevator Cabin */}
            <motion.div
              className="absolute left-3 right-3 h-10 rounded-lg overflow-hidden shadow-lg"
              style={{
                background: "linear-gradient(180deg, hsl(var(--accent)) 0%, hsl(var(--accent)/0.7) 100%)",
              }}
              animate={{
                y: [8, 70, 8],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              {/* Cabin doors */}
              <div className="absolute inset-1.5 flex gap-0.5">
                <motion.div
                  className="flex-1 bg-primary/40 rounded-sm"
                  animate={{ scaleX: [1, 0.1, 1] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    times: [0, 0.5, 1],
                  }}
                  style={{ transformOrigin: "left" }}
                />
                <motion.div
                  className="flex-1 bg-primary/40 rounded-sm"
                  animate={{ scaleX: [1, 0.1, 1] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    times: [0, 0.5, 1],
                  }}
                  style={{ transformOrigin: "right" }}
                />
              </div>
              
              {/* Cabin light */}
              <motion.div
                className="absolute top-0.5 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-primary-foreground/70"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            </motion.div>

            {/* Glow effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-accent/20 to-transparent"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

        {/* Logo */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <motion.h1
            className="font-heading text-5xl md:text-6xl font-bold text-primary-foreground tracking-wider"
            animate={{
              textShadow: [
                "0 0 20px hsl(var(--accent)/0.3)",
                "0 0 40px hsl(var(--accent)/0.5)",
                "0 0 20px hsl(var(--accent)/0.3)",
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            LIFTCO
          </motion.h1>
          <motion.p
            className="text-primary-foreground/60 text-sm tracking-[0.3em] mt-2 uppercase"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Elevator Solutions
          </motion.p>
        </motion.div>

        {/* Loading bar */}
        <motion.div
          className="w-64 h-1 bg-primary-foreground/10 rounded-full overflow-hidden backdrop-blur-sm"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{
              background: "linear-gradient(90deg, hsl(var(--accent)), hsl(var(--accent)/0.6), hsl(var(--accent)))",
            }}
            initial={{ x: "-100%" }}
            animate={{ x: "100%" }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>

        {/* Loading dots */}
        <motion.div
          className="flex items-center gap-2 mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-accent"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* Bottom accent line */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.3, duration: 1 }}
      />
    </motion.div>
  );
};

export default LoadingScreen;
